import AttachmentUpload from 'util/attachment_upload'
import { hideDropZone } from 'util/drop_zone'

import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getRoutes } from 'reduxes/slices/routes/selectors'
import {
  useDispatchDelegateStore,
  useSelectorDelegateStore,
} from 'reduxes/stores/root_store/hooks'
import adminApiRoutes from 'routes/admin_api_routes.json'
import liteApiRoutes from 'routes/lite_api_routes.json'
import trix from 'trix'

import { AxiosContext } from '../../contexts/axios_context'
import { createLiveChatAccount } from '../../reduxes/slices/accounts/actions'
import { setApiError } from '../../reduxes/slices/api_errors/actions'
import {
  getCurrentAccount,
  getCurrentMemberId,
} from '../../reduxes/slices/current/selectors'
import { isLiveChatApp } from '../../reduxes/slices/live_chat_app/selectors'
import { getSelectedTimelineId } from '../../reduxes/slices/selected/selectors'

export const useTrixFileUploading = (
  id,
  editor,
  trixHtml,
  getEditorFromStorage,
  saveEditorToStorage,
) => {
  const dispatch = useDispatch()
  const dispatchRootStore = useDispatchDelegateStore()
  const [uploadingAttachments, setUploadingAttachments] = useState({})
  const [XHR, setXHR] = useState({})
  const currentMemberId = useSelector(getCurrentMemberId)
  const axios = useContext(AxiosContext)
  const disableDropZone = useRef(false)

  const noOfUploadingAttachments = uploadingAttachments?.[id]
    ? Object.keys(uploadingAttachments[id]).length
    : 0

  const routes = useSelectorDelegateStore(getRoutes)
  const selectedTimelineId = useSelector(getSelectedTimelineId)
  const account = useSelector(getCurrentAccount)
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  useEffect(() => {
    const notifyIfAttachmentsUploading = (event) => {
      if (Object.keys(uploadingAttachments).length) {
        event.returnValue =
          'You have a file/image upload in progress. Leaving will cancel the upload.'
      }
    }

    window.addEventListener('beforeunload', notifyIfAttachmentsUploading)

    return () => {
      window.removeEventListener('beforeunload', notifyIfAttachmentsUploading)
    }
  }, [uploadingAttachments])

  useEffect(() => {
    disableDropZone.current = !!selectedTimelineId && !trixHtml
  }, [selectedTimelineId, trixHtml])

  const addAttachmentToState = useCallback(
    (editorId, attachment, attributes, updating) => {
      setUploadingAttachments((prevState) => {
        const attachmentExists = prevState[editorId]?.[attachment.id]
        if (!updating || attachmentExists) {
          return {
            ...prevState,
            [editorId]: {
              ...prevState[editorId],
              [attachment.id]: {
                attachment,
                ...(attributes ? { attributes } : {}),
              },
            },
          }
        } else {
          return prevState
        }
      })
    },
    [],
  )

  const removeAttachmentFromState = useCallback((editorId, id) => {
    setUploadingAttachments((prev) => {
      if (prev[editorId]) {
        const { [id]: removed, ...newState } = prev[editorId]
        if (removed) {
          if (Object.keys(newState).length) {
            return {
              ...prev,
              [editorId]: newState,
            }
          } else {
            const { [editorId]: _, ...newEditorState } = prev
            return newEditorState
          }
        } else {
          return prev
        }
      } else {
        return prev
      }
    })
  }, [])

  useEffect(() => {
    Object.keys(uploadingAttachments).forEach((editorId) => {
      const keys = Object.keys(uploadingAttachments[editorId])
      for (let i = 0; i < keys.length; i++) {
        const attachmentId = keys[i]
        const { attachment, attributes } =
          uploadingAttachments[editorId][attachmentId]
        if (!attributes) continue

        if (editorId === id) {
          const matchingAttachment = editor
            .getDocument()
            .getAttachments()
            .find((i) => i.attributes.values.prevId === attachment.id)
          matchingAttachment.setAttributes(attributes)
          removeAttachmentFromState(editorId, attachment.id)
        } else {
          const editorObj = getEditorFromStorage(editorId)
          if (editorObj) {
            const doc = trix.Document.fromJSON(editorObj.document)
            const matchingAttachment = doc
              .getAttachments()
              .find((i) => i.attributes.values.prevId === attachment.id)
            matchingAttachment.setAttributes(attributes)
            editorObj.document = doc
            saveEditorToStorage(editorId, editorObj)
            removeAttachmentFromState(
              editorId,
              matchingAttachment.attributes.values.prevId,
            )
          }
        }
      }
    })
  }, [
    editor,
    uploadingAttachments,
    id,
    saveEditorToStorage,
    getEditorFromStorage,
    removeAttachmentFromState,
  ])

  useEffect(() => {
    if (Object.keys(uploadingAttachments).length) {
      const notifyIfAttachmentsUploading = (event) => {
        event.returnValue =
          'You have a file/image upload in progress. Leaving will cancel the upload.'
      }
      window.addEventListener('beforeunload', notifyIfAttachmentsUploading)
      return () => {
        window.removeEventListener('beforeunload', notifyIfAttachmentsUploading)
      }
    }
  }, [uploadingAttachments])

  const onUploadEnd = useCallback(
    (editorId, attachment, error, attributes) => {
      if (error) {
        removeAttachmentFromState(editorId, attachment.id)
        attachment.remove()
        dispatch(
          setApiError({
            httpMethod: 'post',
            route: adminApiRoutes.admin_api_v1_files.path,
            error: error,
          }),
        )
      } else {
        const url = routes.rails_service_blob.path
          .replace(':signed_id', attributes.signed_id)
          .replace(':filename', encodeURIComponent(attributes.filename))
        const attributesToAdd = {
          sgid: attributes.attachable_sgid,
          url: liveChatApp
            ? window.speakeasySettings.apiBase + url
            : url,
        }
        if (attachment.file.type.includes('audio')) {
          attributesToAdd[
            'content'
          ] = `<audio controls src=${attributesToAdd.url}></audio>`
        }
        addAttachmentToState(editorId, attachment, attributesToAdd, true)
      }
    },
    [
      addAttachmentToState,
      dispatch,
      liveChatApp,
      removeAttachmentFromState,
      routes.rails_service_blob.path,
    ],
  )

  const handleAttachmentAdd = useCallback(
    async (event) => {
      if (liveChatApp && !currentMemberId) {
        await createLiveChatAccount(axios, dispatchRootStore, dispatch)
      }
      const { attachment } = event
      if (attachment.file) {
        if (attachment.file.type.includes('audio')) {
          attachment.attachment.attributes.values['content'] = ''
        }
        attachment.setAttributes({ prevId: attachment.id })
        addAttachmentToState(id, attachment)
        const url = liveChatApp
          ? window.speakeasySettings.apiBase +
            liteApiRoutes.lite_api_v1_root.path +
            liteApiRoutes.lite_api_v1_files.path
          : adminApiRoutes.admin_api_v1_root.path +
            adminApiRoutes.admin_api_v1_files.path
        const upload = new AttachmentUpload(
          attachment,
          url,
          onUploadEnd,
          (newAttachment, xhr) => {
            setXHR((prevState) => ({
              ...prevState,
              [newAttachment.attachment.id]: xhr,
            }))
          },
          id,
          liveChatApp
        )
        upload.start()
      }
    },
    [
      addAttachmentToState,
      axios,
      currentMemberId,
      dispatch,
      dispatchRootStore,
      id,
      liveChatApp,
      onUploadEnd,
    ],
  )

  const handleAttachmentRemove = useCallback(
    (event) => {
      const { attachment } = event
      XHR[attachment.id]?.abort()
      setXHR((prevState) => {
        const { [attachment.id]: _, ...newState } = prevState
        return newState
      })
      removeAttachmentFromState(id, attachment.id)
    },
    [XHR, id, removeAttachmentFromState],
  )

  useEffect(() => {
    window.addEventListener(
      'trix-attachment-remove',
      handleAttachmentRemove,
      false,
    )
    return () => {
      window.removeEventListener(
        'trix-attachment-remove',
        handleAttachmentRemove,
        false,
      )
    }
  }, [handleAttachmentRemove])

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault()
      if (disableDropZone.current) {
        hideDropZone()
        return
      }

      Array.from(e.dataTransfer.files).forEach((file) => {
        editor.insertFile(file)
      })
      hideDropZone()
    },
    [editor],
  )

  const handleFileAccept = useCallback(
    (event) => {
      const maxFileSize = account?.max_file_size_b || 500 * 1024 * 1024
      if (event.file.size > maxFileSize) {
        event.preventDefault()
        dispatch(
          setApiError({
            httpMethod: 'post',
            route: adminApiRoutes.admin_api_v1_files.path,
            error: {
              error_message: 'File size too large',
              status: 'bad_request',
              validation_errors: null,
            },
          }),
        )
      }
    },
    [account?.max_file_size_b, dispatch],
  )

  return {
    noOfUploadingAttachments,
    handleAttachmentAdd,
    handleAttachmentRemove,
    handleDrop,
    handleFileAccept,
  }
}
